<template>
  <div class="flex flex-col gap-2 bg-muted p-3 rounded">
    <div class="flex flex-col gap-2">
      <div class="w-full">
        <RouterLink :to="toEvent(item.event)" class="hover:underline block overflow-hidden">
          <span v-if="item.event.event_name.match(' vs ')" class="font-semibold block text-base truncate">
            {{ item.event.event_name.match("^(.*?) vs ")[1] }}
            vs
            {{ item.event.event_name.match(" vs (.*?)$")[1] }}
          </span>
          <span v-else class="font-semibold text-base">{{ item.event.event_name }}</span>
        </RouterLink>
      </div>
      <div class="flex gap-1 items-center cursor-pointer text-sm" @click="eventDetailsOpen = !eventDetailsOpen">
        {{ $t("Show event details") }} <ChevronDownIcon class="size-4" :class="{ 'rotate-180': eventDetailsOpen }" />
      </div>
      <div v-if="eventDetailsOpen" class="grid grid-cols-1 gap-2 text-sm">
        <div class="flex gap-1 items-center">
          <ArrowTurnDownRightIcon class="size-3" />
          <div>{{ item.event.tournament_name }} {{ item.event.season }}</div>
        </div>
        <div class="flex gap-1 items-center">
          <ArrowTurnDownRightIcon class="size-3" />{{ formatDates(item.event.date_start, item.event.date_stop) }}
          <div
            v-if="!item.event.date_confirmed"
            class="rounded-full border border-border px-1.5 text-muted-foreground bg-accent">
            {{ $t("Not confirmed") }}
          </div>
        </div>
        <div class="flex gap-1 items-center">
          <ArrowTurnDownRightIcon class="size-3" />
          {{ item.ticket.ticket_title }}
          -
          <span class="span">{{ formatDays(item.ticket.ticket_validfrom, item.ticket.ticket_validuntil) }}</span>
        </div>
        <div class="flex gap-1 items-center">
          <ArrowTurnDownRightIcon class="size-3" />{{ item.event.venue_name }} {{ item.event.city }}
        </div>
        <div v-if="payDetails?.vat_amount && (warning || reverseChargeApplied)" class="flex gap-1 items-center">
          <ArrowTurnDownRightIcon class="size-3" /> {{ $t("Ticket VAT") }}{{ reverseChargeApplied ? "*" : "" }}
          <Euros :amount="payDetails.vat_amount" class="text-sm" />
        </div>
        <div v-if="warning" class="flex gap-1 items-center"><ArrowTurnDownRightIcon class="size-3" />{{ warning }}</div>
      </div>
      <div v-if="userStore.selectedCurrency !== 'EUR'" class="flex w-1/4 justify-center items-baseline">
        <div class="flex gap-2 items-center pl-4 **:font-bold">
          <span>{{ item.amount }} </span>
          <span> &times;</span>
          <Euros :amount="item.ticket.local_rates.net_rate_eur" :calc-fx-rate="true" />
        </div>
      </div>
      <div class="grid grid-cols-2 gap-2 items-center">
        <button
          data-testid="remove-all-button"
          class="flex items-center gap-1 text-sm"
          @click.prevent="cartStore.updateTicketAmount(item.event, item.ticket, 0)">
          <TrashIcon class="size-4" />
          {{ $t("Remove all") }}
        </button>
        <div class="flex items-center gap-1 justify-end">
          <ItemCountInput v-if="cartStore.allocationId" :item="item" />
          <TicketCountInput v-else :item="item" :ticket="item.ticket" :event="item.event" />
        </div>
      </div>
      <Select
        v-if="ableToReleaseAllocation"
        :model-value="isReleased"
        :options="[
          { value: false, label: 'Retain remaining allocation' },
          { value: true, label: 'Release remaining allocation' },
        ]"
        @update:model-value="(event) => (event ? releaseAllocation() : retainAllocation())" />
    </div>

    <div
      v-if="isPaperTicket(item)"
      class="lg:rounded mt-2 mb-4 text-base p-4 border-amber-500 bg-amber-300 text-amber-900">
      {{
        $t(
          "Note: your booking contains paper tickets. A couple of weeks before the event these tickets will be sent to you."
        )
      }}
    </div>

    <div v-if="isCollect(item)" class="lg:rounded mt-2 mb-4 text-base p-4 border-amber-500 bg-amber-300 text-amber-900">
      {{
        $t(
          "Note: your booking contains paper tickets. You or your client needs to pick up the tickets. You will receive more information from us later on."
        )
      }}
    </div>

    <div v-if="isOther(item)" class="lg:rounded mt-2 mb-4 text-base p-4 border-amber-500 bg-amber-300 text-amber-900">
      {{
        $t(
          "Note: it is unclear in what form these tickets will be delivered by the supplier. You will receive more information from us later on."
        )
      }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterLink } from "vue-router";
import { toEvent } from "@/components/event";
import { ref } from "vue";
import { Select } from "@xs2event/js-lib/components";
const userStore = useUserStore();
import { formatDays, formatDates } from "@/helpers/format.js";
import { useUserStore } from "@/store/user.js";
import { ArrowTurnDownRightIcon, TrashIcon } from "@heroicons/vue/24/outline";
import Euros from "@/components/Euros.vue";
import { TicketCountInput, ItemCountInput } from ".";
import { useCartStore } from "@/store/cart.ts";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";

const cartStore = useCartStore();

const eventDetailsOpen = ref(true);

import { computed } from "vue";

import { useI18n } from "vue-i18n";
import {
  markForReleaseAllocation,
  unmarkForReleaseAllocation,
  isToBeReleasedFromAllocation,
} from "@/helpers/reservations";

const { t } = useI18n();

const isCollect = (item) => item.type_ticket === "collect";
const isOther = (item) => item.type_ticket === "other";
const isPaperTicket = (item) => item.type_ticket === "paper" || item.type_ticket === "paperticket";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const props = defineProps<{ item: any }>();

const payDetails = computed(() =>
  cartStore.payDetails?.tickets.find((x) => x.ticket_id == props.item.ticket.ticket_id)
);

const reverseChargeApplied = computed(() => {
  return payDetails.value.reverse_charge_applied;
});

const ableToReleaseAllocation = computed(() => {
  return cartStore.allocationId && props.item.amount != props.item.allocation;
});

const isReleased = isToBeReleasedFromAllocation(props.item.ticket_id);

const releaseAllocation = () => {
  markForReleaseAllocation(cartStore.allocationId!, props.item.ticket_id);
};

const retainAllocation = () => {
  unmarkForReleaseAllocation(cartStore.allocationId!, props.item.ticket_id);
};

const warning = computed(() => {
  if (payDetails.value?.local_vat_number) {
    if (payDetails.value.vat_category == "low" || payDetails.value.vat_category == "high") {
      return t("Local VAT is is applicable");
    } else {
      return t("Margin scheme for travel agencies is applicable");
    }
  }
});
</script>
